import {TemplateVariable} from '../../../generated/graphql';
import {isSingleVarString} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import {JsonObject} from '../utilTypes';

export const substituteVariables = (
  config: JsonObject,
  variables: Record<string, number | string>
): JsonObject => {
  function injectVarsIntoString(str: string): string | number {
    let singleValue: string | number | null = null;
    const replaceString = str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
      singleValue = variables[varName];
      return variables[varName]?.toString() ?? match;
    });
    if (isSingleVarString(str) && singleValue != null) {
      return singleValue;
    }
    return replaceString;
  }

  function substitute(obj: any): any {
    if (typeof obj === 'string') {
      return injectVarsIntoString(obj);
    } else if (typeof obj === 'number') {
      return obj;
    } else if (Array.isArray(obj)) {
      return obj.map(item => substitute(item));
    } else if (typeof obj === 'object' && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key, substitute(val)])
      );
    }
    return obj;
  }

  return substitute(config);
};

export const getDefaultVariableValuesFromQueueVariables = (
  variables: TemplateVariable[]
) => {
  const z = variables.reduce((acc, variable) => {
    const parsedSchema = JSON.parse(variable.schema);
    return {
      ...acc,
      ...(parsedSchema.default != null && {
        [variable.name]: parsedSchema.default,
      }),
    };
  }, {});
  return z;
};

export const getRelevantVariables = (
  serviceConfig: JsonObject,
  allVariables: TemplateVariable[]
) => {
  const configString = JSON.stringify(serviceConfig);
  const variableMatches = configString.match(/\{\{(\w+)\}\}/g) || [];
  const usedVariableNames = new Set(
    variableMatches.map(match => match.replace(/[{}]/g, ''))
  );

  return allVariables.filter(variable => usedVariableNames.has(variable.name));
};

/**
 * Validates that all required services from the job definition are available in the queue config.
 *
 * Args:
 *     jobServices: Dictionary mapping service names to "always" or "never"
 *     queueConfig: The full configuration object from the selected queue
 *
 * Returns:
 *     bool: True if all "always" services are available in the queue config
 *
 * Examples:
 *     validateRequiredServices({"gpu": "always", "storage": "never"}, {"gpu": {...}, "cpu": {...}})
 *     // Returns true because "gpu" (marked as "always") is available
 *
 *     validateRequiredServices({"gpu": "always", "tpu": "always"}, {"gpu": {...}})
 *     // Returns false because "tpu" (marked as "always") is missing
 */
export const validateRequiredServices = (
  jobServices: Record<string, string>,
  queueConfig: Record<string, any> | undefined
): boolean => {
  if (!queueConfig) {
    return Object.keys(jobServices).length === 0;
  }

  const requiredServices = Object.entries(jobServices)
    .filter(([_, requirement]) => requirement === 'always')
    .map(([serviceName, _]) => serviceName);

  return requiredServices.every(serviceName => serviceName in queueConfig);
};

/**
 * Gets the list of missing required services for display in error messages.
 *
 * Args:
 *     jobServices: Dictionary mapping service names to "always" or "never"
 *     queueConfig: The full configuration object from the selected queue
 *
 * Returns:
 *     string[]: Array of service names that are marked as "always" but missing from queue config
 *
 * Examples:
 *     getMissingRequiredServices({"gpu": "always", "tpu": "always"}, {"gpu": {...}})
 *     // Returns ["tpu"]
 */
export const getMissingRequiredServices = (
  jobServices: Record<string, string>,
  queueConfig: Record<string, any> | undefined
): string[] => {
  if (!queueConfig) {
    return Object.entries(jobServices)
      .filter(([_, requirement]) => requirement === 'always')
      .map(([serviceName, _]) => serviceName);
  }

  return Object.entries(jobServices)
    .filter(
      ([serviceName, requirement]) =>
        requirement === 'always' && !(serviceName in queueConfig)
    )
    .map(([serviceName, _]) => serviceName);
};
